import React, { useState } from 'react';
import { CloseableModal } from '../../../../components/CloseableModal';
import { AlertObject } from '../../../../components/Alert';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { HrisProvider } from '../../../../enums/IntegrationProvider/HrisProvider';
import { getHrisName } from '../../../../utils/hris';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { ModalBody } from './ModalBody';
import { LocationEntity } from '../../../../services/GeoLocationService';
import { queryClient } from '../../../../config/queryClient';
import { QueryClientProvider } from 'react-query';
import { useApplication } from '../../../../queries/v1/useApplication';
import { useSurveyRequest } from '../../../../queries/v1/reference_check/useSurveyRequest';
import { useBackgroundCheck } from '../../../../queries/v1/reference_check/useBackgroundCheck';

interface PropTypes {
  applicationIds: number[];
  applicationId: number;
  allowedHrisProvider: HrisProvider;
  isOpen: boolean;
  onClose: (succeeded: boolean) => void;
  setAlert: (alert: AlertObject) => void;
}

interface DocumentFile {
  id: number;
  fileName: string;
  fileContent: string;
}

interface FormValuesSelectOption {
  value: string;
  label: string;
}

export interface PushToHrisFormValues {
  firstName: string;
  lastName: string;
  personalEmail: string;
  workEmail: string;
  phoneNumber: string;
  location: LocationEntity;
  office: FormValuesSelectOption;
  jobName: string;
  department: FormValuesSelectOption;
  resume: DocumentFile;
  coverLetter: DocumentFile;
  referenceCheck: DocumentFile;
  backgroundCheck: DocumentFile;
  startDate: Date;
}

export function EditablePushToHrisModal(props: PropTypes) {
  if (!props.isOpen) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <ModalUI {...props} />
    </QueryClientProvider>
  );
}

function ModalUI(props: PropTypes) {
  const [isLoading, setIsLoading] = useState<boolean>(undefined);

  const { data: application, isLoading: isLoadingApplication } = useApplication(
    props.applicationId,
    [
      'candidate',
      'job',
      'job.department',
      'application_resume',
      'application_cover_letter',
      'job_post',
      'job_post.location',
    ],
  );

  const { data: surveyRequest, isLoading: isLoadingSurveyRequest } =
    useSurveyRequest({
      id: null,
      applicationExternalId: props.applicationId,
    });

  const { data: backgroundCheck, isLoading: isLoadingBackgroundCheck } =
    useBackgroundCheck({
      id: null,
      applicationExternalId: props.applicationId,
    });

  const isDataLoading =
    isLoading ??
    (isLoadingApplication ||
      isLoadingSurveyRequest ||
      isLoadingBackgroundCheck);

  return isDataLoading ? (
    <LoadingSpinner />
  ) : (
    <CloseableModal
      className={classNames('modal-dialog-centered', styles['modal'])}
      onClose={() => props.onClose(null)}
      isOpen={true}
      size='md'
      closeDisabled={false}
      keyboard={true}
      backdrop={true}
      headerTitle={`Export to ${getHrisName(props.allowedHrisProvider)}`}
      bodyChildren={
        <>
          {isDataLoading && <LoadingSpinner showBackdrop={true} />}
          <ModalBody
            surveyRequest={surveyRequest}
            application={application}
            applicationIds={props.applicationIds}
            backgroundCheck={backgroundCheck}
            setAlert={props.setAlert}
            setIsLoading={setIsLoading}
            hrisProvider={props.allowedHrisProvider}
            onClose={props.onClose}
          />
        </>
      }
    />
  );
}
